import { PermissionAction, PermissionResource, useGetUser } from '@sib/api';
import { useAutoLogout } from '@sib/auth';
import { useCan } from '@sib/auth';
import { tracking, usePageTracking } from '@sib/tracking';
import { FileUploadMode } from '@sib/types';
import { cn } from '@sib/ui';
import {
  FileDropDialog,
  FileUploadContext,
  SpendbrainDialog,
  UploadQueueGuardDialog,
} from '@spendbrain/common';
import {
  useFileDropDialog,
  useUploadQueueGuardDialog,
} from '@spendbrain/common/hooks';
import { LayoutProps } from '@spendbrain/common/types/layout';
import { updateUserId } from '@spendbrain/common/utils';
import { MEDIA_SM_QUERY } from '@spendbrain/config/constants';
import { useMediaQuery, useExternalToken } from '@spendbrain/hooks';
import { useFileManagerFileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { useSpendbrainVersionCheck } from '@spendbrain/utils/version';
import { useFeedbackStore, useSidebarStore } from '../../stores';
import { FeedbackDialog } from '../FeedbackDialog';
import { NavContent } from '../NavContent';
import { NavDrawer } from '../NavDrawer';
import { NewVersionDialog } from '../NewVersionDialog';
import { PhoneNumberModal } from '../PhoneNumberModal';
import { TopBar, TOPBAR_HEIGHT } from '../TopBar';
import {
  useOrganizationSetup,
  useBeforeUnloadWarning,
  usePhoneNumberModal,
} from './DefaultLayout.utils';

export const DefaultLayout = ({ children }: LayoutProps) => {
  useOrganizationSetup();
  useExternalToken();
  usePageTracking();
  useBeforeUnloadWarning();
  useAutoLogout({ onLogout: updateUserId });
  const { isOpen, toggleSidebar, setSidebarOpen } = useSidebarStore();
  const { closeFeedbackDialog, isFeedbackDialogOpen } = useFeedbackStore();
  const isMobile = useMediaQuery(MEDIA_SM_QUERY);
  const { files } = useFileManagerFileUploadStore();
  const { isNewVersionAvailable, onAppReload } = useSpendbrainVersionCheck();
  const { data: user, refetch: refetchUser } = useGetUser();
  const {
    getInputProps,
    getRootProps,
    closeDialog,
    currentMode,
    onModeChange,
    openDialog,
  } = useFileDropDialog();
  const can = useCan();
  const {
    isOpen: isPhoneNumberModalOpen,
    handleSuccess: handlePhoneNumberSuccess,
  } = usePhoneNumberModal({ user, refetchUser });
  const { isDialogOpen: isUploadQueueGuardDialogOpen, dialogProps } =
    useUploadQueueGuardDialog();

  const openFileDropDialog = () => {
    openDialog(
      files.length ? FileUploadMode.FileManager : FileUploadMode.Dropzone
    );
    tracking.track('Opened upload file dialog');
    tracking.startSessionRecording();
  };

  const openFileManager = () => {
    dialogProps.onClose();
    openDialog(FileUploadMode.FileManager);
  };

  return (
    <div className="flex h-screen flex-col" {...getRootProps()}>
      <input {...getInputProps()} />
      <FeedbackDialog
        open={isFeedbackDialogOpen}
        onClose={closeFeedbackDialog}
      />
      <UploadQueueGuardDialog
        {...dialogProps}
        open={isUploadQueueGuardDialogOpen}
        onOpenFileManager={openFileManager}
      />
      <SpendbrainDialog />
      <NewVersionDialog open={isNewVersionAvailable} onReload={onAppReload} />
      {user?.id && (
        <PhoneNumberModal
          open={isPhoneNumberModalOpen}
          userId={user.id}
          onSuccess={handlePhoneNumberSuccess}
        />
      )}
      <FileUploadContext.Provider value={{ openDialog: openFileDropDialog }}>
        <TopBar onNavOpen={toggleSidebar} onUploadClick={openFileDropDialog} />
        <div className="flex flex-1" style={{ paddingTop: TOPBAR_HEIGHT }}>
          {isMobile ? (
            <NavDrawer onNavOpen={setSidebarOpen} isOpen={isOpen}>
              <NavContent />
            </NavDrawer>
          ) : (
            <div
              style={{ paddingTop: TOPBAR_HEIGHT }}
              className={cn(
                'fixed top-0 left-0 z-40 h-full w-64 transform bg-white transition-transform duration-700 ease-in-out',
                isOpen ? 'translate-x-0' : '-translate-x-64'
              )}
            >
              <NavContent />
            </div>
          )}
          {isOpen && !isMobile && (
            <div
              className="fixed inset-0 z-30 hidden bg-black opacity-25 sm:block lg:hidden"
              onClick={() => setSidebarOpen(false)}
            ></div>
          )}
          {can({
            permissions: {
              actions: PermissionAction.CREATE,
              resources: PermissionResource.Documents,
            },
          }) && (
            <FileDropDialog
              onClose={closeDialog}
              onModeChange={onModeChange}
              currentMode={currentMode}
            />
          )}
          <main
            className={cn(
              'flex-1 transition-all duration-700 ease-in-out',
              'mp-mask', // for mixpanel session recording masking
              isOpen ? 'w-[calc(100%-256px)] lg:ml-64' : 'ml-0 w-full'
            )}
          >
            {children}
          </main>
        </div>
      </FileUploadContext.Provider>
    </div>
  );
};

DefaultLayout.displayName = 'DefaultLayout';
